import { apiFetch, setSessionTokens, clearSessionTokens, getAccessToken, getRefreshToken, IDENTIDAD_MS } from "./client";

export type LoginInput = {
  email: string;
  password: string;
};

export type LoginResponse = {
  accessToken: string;
  refreshToken?: string;
  user: {
    id: string;
    email: string;
    displayName: string;
  };
};

export type UserProfile = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  status?: string;
  /** Panel de plataforma (/admin). Lo decide el backend, migración 065. */
  isPlatformAdmin?: boolean;
};

/** Membresía REAL en el contexto activo (flag canónico del backend). */
export type ContextMembership = "player" | "fan" | "browse_only" | "spectator";

export type ActiveContext = {
  league: { id: string; name: string };
  tournament?: { id: string; name: string } | null;
  team?: { id: string; name: string } | null;
  /** Roles del usuario EN ESA liga (context_roles): organizer, player,
   *  captain, referee, fan, admin. Decide a qué shell mandarlo. */
  roles?: string[];
  /** "¿Juego ACÁ?" — player | fan (equipo sin ficha) | browse_only (ficha en
   *  la liga, contexto sin equipo) | spectator. Gatea Equipo/Mi Tarjeta. */
  membership?: ContextMembership | null;
  /** Es el DT vigente de su equipo (138). membership viene "player" para que
   *  tenga su equipo; esto dice que se lo muestre como DT. */
  isCoach?: boolean;
  /** DT que además juega en el equipo (jugador-DT). */
  coachAlsoPlays?: boolean;
};

export type FullProfileResponse = {
  user: UserProfile;
  /** Ficha de jugador en ALGUNA liga. YA NO es el gate de Comunidad: desde el
   *  01-11-2026 escribir ahí sólo pide sesión (assertCommunityWriter), para
   *  que el que todavía no tiene equipo pueda publicar que lo busca. */
  hasPlayerFicha?: boolean;
  // null cuando el usuario todavía no tiene liga (recién registrado) → onboarding.
  activeContext: ActiveContext | null;
};

/** ¿Juega en el contexto activo? Backend nuevo manda `membership`; con uno
 *  viejo caemos a "tiene equipo en el contexto" (la heurística histórica). */
export function esJugadorDelContexto(ctx: ActiveContext | null | undefined): boolean {
  if (!ctx) return false;
  if (ctx.membership != null) return ctx.membership === "player";
  return ctx.team?.id != null;
}

/** ¿Organiza la liga del contexto? El superusuario ya llega con "organizer"
 *  en `roles` (loadContextRoles). Le abre Mi Tarjeta en el Modo Jugador. */
export function esOrganizadorDelContexto(ctx: ActiveContext | null | undefined): boolean {
  return (ctx?.roles ?? []).some((r) => r.toLowerCase() === "organizer");
}

export async function login(input: LoginInput): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    body: input,
    withAuth: false,
  });
  setSessionTokens({
    accessToken: res.accessToken,
    refreshToken: res.refreshToken ?? null,
  });
  return res;
}

export async function loginWithGoogle(idToken: string): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>("/api/v1/auth/google", {
    method: "POST",
    body: { idToken },
    withAuth: false,
  });
  setSessionTokens({
    accessToken: res.accessToken,
    refreshToken: res.refreshToken ?? null,
  });
  return res;
}

/**
 * Ingreso con Apple. A diferencia de Google, esto NO siempre termina en una
 * sesión: Apple deja esconder el mail detrás de un alias privado
 * (`…@privaterelay.appleid.com`), así que cuando el mail no coincide con
 * ninguna cuenta el backend **no inventa nada** y devuelve `falta_decidir` con
 * un vale firmado. Ahí hay que preguntarle a la persona si ya tiene cuenta
 * (sigue por `appleEngancharPorDocumento`) o si es nueva (`appleCrearCuenta`).
 *
 * El `nombre` lo manda el cliente porque Apple lo entrega UNA sola vez, en el
 * primer ingreso y fuera del token; el backend sólo lo usa para rellenar el
 * nombre visible de una cuenta nueva.
 */
export type RespuestaDeApple =
  | { estado: "listo"; sesion: LoginResponse }
  | { estado: "falta_decidir"; vale: string; mail: string | null; esAliasPrivado: boolean };

function guardarSesion(res: LoginResponse): LoginResponse {
  setSessionTokens({
    accessToken: res.accessToken,
    refreshToken: res.refreshToken ?? null,
  });
  return res;
}

export async function loginWithApple(
  identityToken: string,
  nombre?: string | null,
): Promise<RespuestaDeApple> {
  const res = await apiFetch<RespuestaDeApple>("/api/v1/auth/apple", {
    method: "POST",
    body: { identityToken, nombre: nombre ?? null },
    withAuth: false,
  });
  if (res.estado === "listo") guardarSesion(res.sesion);
  return res;
}

/**
 * "Sí, ya tengo cuenta", en dos pasos. El documento identifica a la persona
 * (es como FORJA identifica, [[documento = identidad]]) pero NO la prueba: lo
 * ve el organizador, el capitán, está en la ficha. Así que el documento sólo
 * dice a qué mail mandar un código, y el código es lo que vincula. Sin esto,
 * cualquiera con un Apple ID se quedaba con la cuenta de un compañero
 * sabiéndole el documento.
 */
export type RespuestaDeEnganche = {
  estado: "codigo_enviado";
  /** Para el segundo paso. Dura 10 minutos. */
  vale: string;
  /** Enmascarado (`r•••@gmail.com`): para que reconozca su casilla, nada más. */
  mail: string;
};

export async function appleEngancharPorDocumento(
  vale: string,
  documento: string,
): Promise<RespuestaDeEnganche> {
  return apiFetch<RespuestaDeEnganche>("/api/v1/auth/apple/enganchar", {
    method: "POST",
    body: { vale, documento },
    withAuth: false,
  });
}

/** El código que llegó al mail. Recién acá queda vinculado y hay sesión. */
export async function appleConfirmarCodigo(vale: string, codigo: string): Promise<LoginResponse> {
  const res = await apiFetch<{ estado: "listo"; sesion: LoginResponse }>(
    "/api/v1/auth/apple/enganchar/confirmar",
    { method: "POST", body: { vale, codigo }, withAuth: false },
  );
  return guardarSesion(res.sesion);
}

/** "Soy nuevo": recién acá se crea la cuenta. */
export async function appleCrearCuenta(
  vale: string,
  nombre?: string | null,
): Promise<LoginResponse> {
  const res = await apiFetch<{ estado: "listo"; sesion: LoginResponse }>(
    "/api/v1/auth/apple/nueva-cuenta",
    { method: "POST", body: { vale, nombre: nombre ?? null }, withAuth: false },
  );
  return guardarSesion(res.sesion);
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  try {
    // Enviamos el refresh token para que el backend lo revoque (logout real).
    await apiFetch("/api/v1/auth/logout", {
      method: "POST",
      body: refreshToken ? { refreshToken } : {},
      withAuth: false,
    });
  } catch {
    // ignoramos errores: igual limpiamos localmente
  } finally {
    clearSessionTokens();
  }
}

export async function fetchProfile(): Promise<FullProfileResponse> {
  const res = await apiFetch<FullProfileResponse>("/api/v1/me/profile", { cacheMs: IDENTIDAD_MS });
  if (!res.user) {
    throw new Error("Respuesta de perfil sin campo 'user'");
  }
  return res;
}

export function isLoggedIn(): boolean {
  return getAccessToken() !== null;
}

export type LeagueContext = {
  leagueId: string;
  leagueName: string;
  leagueLogoUrl: string | null;
};

export async function fetchMyLeagues(): Promise<LeagueContext[]> {
  const res = await apiFetch<{ contexts: LeagueContext[] }>("/api/v1/me/contexts", { cacheMs: IDENTIDAD_MS });
  // /me/contexts puede devolver filas duplicadas (un row por rol/torneo/equipo).
  // Aca solo nos interesan las ligas únicas.
  const seen = new Set<string>();
  const out: LeagueContext[] = [];
  for (const c of res.contexts) {
    if (!c.leagueId || seen.has(c.leagueId)) continue;
    seen.add(c.leagueId);
    out.push({
      leagueId: c.leagueId,
      leagueName: c.leagueName,
      leagueLogoUrl: c.leagueLogoUrl ?? null,
    });
  }
  return out;
}

/**
 * Contexto disponible con TODA la info por torneo (liga + torneo + categoría +
 * modalidad + año + estado). `fetchMyLeagues` deduplica y tira los torneos;
 * los selectores "Mis ligas" y "Cambiar torneo" necesitan el detalle para
 * agrupar y subtitular como la app.
 */
export type OrganizerContext = {
  leagueId: string;
  leagueName: string;
  leagueLogoUrl: string | null;
  tournamentId: string | null;
  tournamentName: string | null;
  tournamentStatus: string | null; // active | scheduled | finished | draft | cancelled
  tournamentYear: number | null;
  categoryLabel: string | null; // Libre / +33 / Sub-15 …
  modalityLabel: string | null; // Fútbol 5 / 7 / 8 / 11 / Futsal
  competitionName: string | null; // Competencia (102): "Femenino Martes" …
};

export async function fetchMyContexts(): Promise<OrganizerContext[]> {
  const res = await apiFetch<{ contexts: Array<Record<string, unknown>> }>("/api/v1/me/contexts", {
    cacheMs: IDENTIDAD_MS,
  });
  // Puede venir la misma dupla liga:torneo por más de un rol; nos quedamos con
  // la primera. Las filas sin torneo (contexto a nivel liga) se conservan: son
  // la liga "sin torneos todavía".
  const seenTournament = new Set<string>();
  const out: OrganizerContext[] = [];
  const seenLeagueOnly = new Set<string>();
  for (const c of res.contexts) {
    const leagueId = c.leagueId as string | undefined;
    if (!leagueId) continue;
    const tournamentId = (c.tournamentId as string | null) ?? null;
    if (tournamentId) {
      const key = `${leagueId}:${tournamentId}`;
      if (seenTournament.has(key)) continue;
      seenTournament.add(key);
    } else {
      // Una sola fila "solo liga" por liga alcanza (marca la liga sin torneos).
      if (seenLeagueOnly.has(leagueId)) continue;
      seenLeagueOnly.add(leagueId);
    }
    out.push({
      leagueId,
      leagueName: (c.leagueName as string) ?? "",
      leagueLogoUrl: (c.leagueLogoUrl as string | null) ?? null,
      tournamentId,
      tournamentName: (c.tournamentName as string | null) ?? null,
      tournamentStatus: (c.tournamentStatus as string | null) ?? null,
      tournamentYear: (c.tournamentYear as number | null) ?? null,
      categoryLabel: (c.categoryLabel as string | null) ?? null,
      modalityLabel: (c.modalityLabel as string | null) ?? null,
      competitionName: (c.competitionName as string | null) ?? null,
    });
  }
  return out;
}

export async function setActiveLeague(leagueId: string): Promise<void> {
  await apiFetch("/api/v1/me/active-context", {
    method: "PUT",
    body: { leagueId, tournamentId: null, teamId: null },
  });
}

/* ---------- Alta de cuenta con email (sin Google) ---------- */

export type RegisterInput = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

/**
 * Crea la cuenta y dispara el código de 6 dígitos al mail. NO abre sesión:
 * la cuenta nace 'pending' y se activa recién con verifyEmail().
 */
export async function register(input: RegisterInput): Promise<void> {
  await apiFetch("/api/v1/auth/register", {
    method: "POST",
    body: input,
    withAuth: false,
  });
}

/** Verifica el código del mail: activa la cuenta y devuelve la sesión. */
export async function verifyEmail(email: string, code: string): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>("/api/v1/auth/verify-email", {
    method: "POST",
    body: { email, code },
    withAuth: false,
  });
  setSessionTokens({
    accessToken: res.accessToken,
    refreshToken: res.refreshToken ?? null,
  });
  return res;
}

/**
 * Reenviar el código = volver a llamar register con los mismos datos: si la
 * cuenta sigue 'pending' el backend la reusa y manda un código nuevo
 * (auth.service). No hay endpoint dedicado para esto — email-code/request es
 * para login y reset, no para la verificación del alta.
 */
export async function resendVerification(input: RegisterInput): Promise<void> {
  await register(input);
}

// ── Entrar sin contraseña / recuperarla ───────────────────────────────────
// Los tres endpoints ya existían en el backend y Android los usa desde
// siempre; la web era la que no los ofrecía, así que un organizador o un
// jugador que se registró con mail y olvidó la clave se quedaba afuera.

export type EmailCodePurpose = "login" | "password_reset";

/**
 * Pide el código de 6 dígitos al mail. SIEMPRE resuelve OK aunque la cuenta
 * no exista (el backend no revela si hay usuario, a propósito): la pantalla
 * tiene que decir "si existe, te llegó", nunca "ese mail no está".
 */
export async function requestEmailCode(email: string, purpose: EmailCodePurpose): Promise<void> {
  await apiFetch("/api/v1/auth/email-code/request", {
    method: "POST",
    body: { email, purpose },
    withAuth: false,
  });
}

/** Canjea el pase de un solo uso que emitió la APP y deja la sesión abierta. */
export async function canjearPaseAppWeb(code: string): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>("/api/v1/auth/app-handoff/exchange", {
    method: "POST",
    body: { code },
    withAuth: false,
  });
  setSessionTokens({
    accessToken: res.accessToken,
    refreshToken: res.refreshToken ?? null,
  });
  return res;
}

/** Entra con el código del mail, sin contraseña. Devuelve sesión. */
export async function loginWithEmailCode(email: string, code: string): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>("/api/v1/auth/email-code/verify", {
    method: "POST",
    body: { email, code },
    withAuth: false,
  });
  setSessionTokens({
    accessToken: res.accessToken,
    refreshToken: res.refreshToken ?? null,
  });
  return res;
}

/** Cambia la contraseña con el código del mail y deja la sesión abierta. */
export async function resetPasswordWithCode(
  email: string,
  code: string,
  newPassword: string,
): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>("/api/v1/auth/password-reset/confirm", {
    method: "POST",
    body: { email, code, newPassword },
    withAuth: false,
  });
  setSessionTokens({
    accessToken: res.accessToken,
    refreshToken: res.refreshToken ?? null,
  });
  return res;
}

/**
 * BORRAR LA CUENTA. Apple lo exige desde adentro de la app (regla 5.1.1(v)) y
 * Google pide lo mismo. El detalle de qué se va y qué queda vive en el
 * backend (modules/me/account-deletion.service.ts).
 */
export type PreviewDeBorrado = {
  /** Fichas que quedan vivas en la liga, sin cuenta. */
  fichas: number;
  /** Ligas donde es el ÚNICO organizador: pasan a FORJA. */
  ligasQueQuedanSinOrganizador: { id: string; nombre: string }[];
  publicacionesYComentarios: number;
  mensajes: number;
};

/** Qué se lleva ESTE borrado. La pantalla de confirmación no puede prometer
 * en general: "tu liga queda sin organizador" sólo se sabe mirando su caso. */
export function getPreviewDeBorrado(): Promise<PreviewDeBorrado> {
  return apiFetch<PreviewDeBorrado>("/api/v1/me/account-deletion");
}

export type ResultadoDeBorrado = {
  ok: true;
  ligasAdoptadas: number;
  fichasLiberadas: number;
};

/** Irreversible. El backend lo hace entero en una transacción. */
export function borrarMiCuenta(): Promise<ResultadoDeBorrado> {
  return apiFetch<ResultadoDeBorrado>("/api/v1/me/account", { method: "DELETE" });
}
